import { ScrollTopModule } from 'primeng/scrolltop';
import { SearchService } from './search.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TranslationService } from '../../../../common/service/translate.service';
import { AiFeedbackProblemTypePipe } from './pipe/ai-feedback-problem-type.pipe';
import { EnumConverter } from '../../../../common/helper/enum-converter';
import { FormsModule } from '@angular/forms';
import { Component, signal, computed, effect, OnInit, ViewChild, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { InputGroupModule } from 'primeng/inputgroup';
import { ButtonModule } from 'primeng/button';
import { Dialog, DialogModule } from 'primeng/dialog';
import { AutoFocusModule } from 'primeng/autofocus';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';
import { InputTextModule } from 'primeng/inputtext';
import { ChatMessage, SearchMessageDto } from './model/search-message-dto';
import { SearchRole } from './enum/search-role.enum';
import { DividerModule } from 'primeng/divider';
import { Subscription } from 'rxjs';
import { MarkdownModule } from 'ngx-markdown';
import { SearchResultType } from './enum/search-result-type.enum';
import { SearchResultItem } from './model/search-result-item';
import { SearchStandardResultComponent } from './search-standard-result/search-standard-result.component';
import { SearchDetailResultComponent } from './search-detail-result/search-detail-result.component';
import { SearchAggregateResultComponent } from './search-aggregate-result/search-aggregate-result.component';
import { SearchLicenseResultComponent } from './search-license-result/search-license-result.component';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { AiFeedbackProblemType } from './enum/ai-feedback-problem-type.enum';
import { AiFeedback } from './model/ai-feedback-dto';

import { NgTemplateOutlet } from '@angular/common';
import { UserService } from '../../../../common/service/user.service';
@Component({
  selector: 'gate-searchbar',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    FormsModule,
    TranslatePipe,
    InputGroupModule,
    ButtonModule,
    DialogModule,
    InputIconModule,
    IconFieldModule,
    InputTextModule,
    AutoFocusModule,
    DividerModule,
    MarkdownModule,
    InputGroupModule,
    ScrollTopModule,
    SelectModule,
    TextareaModule,
    TooltipModule,
    SearchStandardResultComponent,
    SearchDetailResultComponent,
    SearchAggregateResultComponent,
    SearchLicenseResultComponent
],
  templateUrl: './searchbar.component.html',
  styleUrl: './searchbar.component.scss'
})
export class SearchbarComponent implements OnInit, OnDestroy {
  private searchService = inject(SearchService);
  private translationService = inject(TranslationService);
  private translateService = inject(TranslateService);
  private userService = inject(UserService);
  private cdr = inject(ChangeDetectorRef);
  public isSearching = signal<boolean>(false);
  public loading = signal<boolean>(false);
  public visible: boolean = false;
  public searchString!: string;
  public messageHistory: SearchMessageDto = new SearchMessageDto();
  private abortController: AbortController | null = null;
  public searchResultTypeEnum = SearchResultType;
  public userMessage: any;
  // public messageHistory: userMessage[] = [];
  private chatSubscription!: Subscription;
  public role = SearchRole;
  public editModeEnable?: number;
  @ViewChild('dialog') dialog!: Dialog;
  public userPrompt!: string;
  public errorMsg = '';
  public isImpersonationInProgress = computed(() => this.userService.isImpersonationInProgress());

  // Feedback dialog state
  public feedbackDialogVisible = false;
  public selectedFeedbackMessage: ChatMessage | null = null;
  public selectedProblemType: AiFeedbackProblemType = AiFeedbackProblemType.Other;
  public feedbackComment: string = '';
  public submittingFeedback = false;

  public problemTypeOptions: any[] = [];

  private mapAiFeedbackProblemTypeEnumToArray(): void {
    const pipe = new AiFeedbackProblemTypePipe();
    const options = EnumConverter.ConvertEnumToArray(AiFeedbackProblemType);
    options.forEach((element: any) => {
      element.key = this.translationService.translateKey(pipe.transform(element.value));
    });
    this.problemTypeOptions = options;
  }

  private getUserPromptForMessage(messageIndex: number): string {
    // Look backwards in message history for the corresponding User prompt
    for (let i = messageIndex; i >= 0; i--) {
      const msg = this.messageHistory.messages[i];
      if (msg && msg.role === SearchRole.User) {
        return msg.content || '';
      }
    }
    return '';
  }

  /**
   * Builds the AI feedback DTO payload containing context prompt, AI answer, and message history.
   * @param message Target AI chat message
   * @param index Message index in search history
   * @param isLiked Whether positive (up) or negative (down) feedback was given
   */
  private buildFeedbackPayload(message: ChatMessage, index: number, isLiked: boolean): AiFeedback {
    const userPrompt = this.getUserPromptForMessage(index);
    const aiAnswer = message.content || message.freeText || message.message || '';
    const payload: AiFeedback = {
      id: message.requestId,
      isLiked: isLiked,
      userMessage: '',
      aiResponse: `User: ${userPrompt}\nAI: ${aiAnswer}`,
    };
    if (!isLiked) {
      payload.messageHistory = JSON.stringify(this.messageHistory.messages);
    }
    return payload;
  }

  /**
   * Helper method to deselect / toggle off previously given feedback.
   * @param message Target AI chat message
   * @param index Message index in search history
   * @param type Feedback type ('up' or 'down')
   */
  private toggleOffFeedback(message: ChatMessage, index: number, type: 'up' | 'down'): void {
    message.submittingFeedbackState = type;
    const feedback = this.buildFeedbackPayload(message, index, type === 'up');

    this.searchService.submitFeedback(feedback).then(
      () => {
        message.submittingFeedbackState = undefined;
        message.feedbackGiven = undefined;
        message.requestId = undefined;
        this.cdr.detectChanges();
      },
      (err) => {
        message.submittingFeedbackState = undefined;
        console.error(`Error deselecting ${type} feedback`, err);
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Handles user click on Thumbs Up (Good Feedback).
   * Toggles upvote state and submits positive feedback.
   * @param message Target AI chat message
   * @param index Message index in search history
   */
  public onThumbsUp(message: ChatMessage, index: number): void {
    if (message.submittingFeedbackState) return;

    // Toggle off if already upvoted
    if (message.feedbackGiven === 'up') {
      this.toggleOffFeedback(message, index, 'up');
      return;
    }

    message.submittingFeedbackState = 'up';
    const feedback = this.buildFeedbackPayload(message, index, true);

    this.searchService.submitFeedback(feedback).then(
      (res) => {
        message.submittingFeedbackState = undefined;
        message.feedbackGiven = 'up';
        if (res) {
          message.requestId = res;
        }
        this.cdr.detectChanges();
      },
      (err) => {
        message.submittingFeedbackState = undefined;
        console.error('Error submitting thumbs up feedback', err);
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Handles user click on Thumbs Down (Bad Feedback).
   * Toggles downvote state, submits negative feedback, and opens the detail feedback dialog.
   * @param message Target AI chat message
   * @param index Message index in search history
   */
  public onThumbsDown(message: ChatMessage, index: number): void {
    if (message.submittingFeedbackState) return;

    // Toggle off if already downvoted
    if (message.feedbackGiven === 'down') {
      this.toggleOffFeedback(message, index, 'down');
      return;
    }

    message.submittingFeedbackState = 'down';
    const feedback = this.buildFeedbackPayload(message, index, false);

    this.searchService.submitFeedback(feedback).then(
      (res) => {
        message.submittingFeedbackState = undefined;
        message.feedbackGiven = 'down';
        if (res) {
          message.requestId = res;
        }
        // Reset and show detail dialog for negative feedback
        this.selectedFeedbackMessage = message;
        this.selectedProblemType = AiFeedbackProblemType.Other;
        this.feedbackComment = '';
        this.feedbackDialogVisible = true;
        this.cdr.detectChanges();
      },
      (err) => {
        message.submittingFeedbackState = undefined;
        console.error('Error submitting thumbs down feedback', err);
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Submits detailed feedback (problem category and optional comments) from the dialog modal.
   */
  public submitDetailedFeedback(): void {
    if (!this.selectedFeedbackMessage) return;

    this.submittingFeedback = true;
    const msgIndex = this.messageHistory.messages.indexOf(this.selectedFeedbackMessage);
    const userPrompt = this.getUserPromptForMessage(msgIndex);
    const aiAnswer = this.selectedFeedbackMessage.content || this.selectedFeedbackMessage.freeText || this.selectedFeedbackMessage.message || '';

    const feedback: AiFeedback = {
      id: this.selectedFeedbackMessage.requestId,
      isLiked: false,
      problemType: this.selectedProblemType,
      userMessage: this.feedbackComment,
      messageHistory: JSON.stringify(this.messageHistory.messages),
      aiResponse: `User: ${userPrompt}\nAI: ${aiAnswer}`,
    };

    this.searchService.submitFeedback(feedback).then(
      () => {
        this.submittingFeedback = false;
        this.feedbackDialogVisible = false;
        this.selectedFeedbackMessage = null;
        this.cdr.detectChanges();
      },
      (err) => {
        console.error('Error submitting detailed feedback', err);
        this.submittingFeedback = false;
        this.feedbackDialogVisible = false;
        this.cdr.detectChanges();
      }
    );
  }

  ngOnInit(): void {
    this.visible = false;
    this.clearSearch();
    this.mapAiFeedbackProblemTypeEnumToArray();
    this.translateService.onLangChange.subscribe(() => {
      this.mapAiFeedbackProblemTypeEnumToArray();
    });
  }

  /**
   * Shows the search dialog.
   */
  showDialog() {
    this.visible = true;
    this.onSearch();
  }

  /**
   * Hides the search dialog and clears search results.
   * Also aborts any ongoing search request.
   */
  hideDialog() {
    this.visible = false;
    this.clearSearch();
  }

  clearSearch(clearSearchString: boolean = true) {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.isSearching.set(false);
    this.loading.set(false);
    this.errorMsg = '';
    this.messageHistory = new SearchMessageDto();
    if (clearSearchString) this.searchString = '';
  }
  /**
   * Performs a search operation with debounce and request cancellation.
   */
  public async onSearch() {
    this.clearSearch(false);
    if (this.searchString?.trim().length != 0) {
      this.isSearching.set(true);
      this.chatWithAI(this.searchString.trim(), false);
    }
  }
  public async chatWithAI(userPrompt: string, isEdit: boolean = false) {
    if (userPrompt?.trim().length != 0 && !this.loading()) {
      this.errorMsg = '';
      this.loading.set(true);

      if (!isEdit) {
        const message = new ChatMessage();
        message.content = userPrompt.trim();
        message.role = SearchRole.User;
        this.messageHistory.messages.push(message);
        this.scrollToBottom();
      }

      this.abortController = new AbortController();
      const { signal } = this.abortController;
      let fullResponse = ''; // Store the full response message
      let agentMessage = new ChatMessage();
      agentMessage.role = SearchRole.Agent;

      // Extract only content and role
      const messages: ChatMessage[] = this.messageHistory.messages.map(({ content, role }) => ({ content, role }));
      const payload: SearchMessageDto = {
        threadId: this.messageHistory.threadId || '',
        messages: messages,
      };
      // Subscribe to chat API stream
      this.chatSubscription = this.searchService.chatWithAI(payload, signal).subscribe({
        next: (response: any) => {
          if (fullResponse == '') {
            this.messageHistory.messages.push(agentMessage);
          }
          // Accumulate streamed response
          fullResponse += response;
          agentMessage.content = fullResponse;
          agentMessage = this.formatMessage(agentMessage);
          this.isSearching.set(false);

          this.cdr.detectChanges();
          this.scrollToBottom();
        },
        complete: () => {
          this.loading.set(false);
          this.scrollToBottom();
        },
        error: (err) => {
          this.errorMsg = '';
          if (err?.message != 'AbortError: The operation was aborted.') this.errorMsg = err?.message;
          console.error('Error:', err);
          this.isSearching.set(false);
          this.loading.set(false);
        },
      });

      // Clear user input field
      if (!isEdit) this.userMessage = '';
    } else {
      this.loading.set(false);
      if (this.abortController) {
        this.abortController.abort();
      }
    }
  }

  private formatMessage(agentMessage: ChatMessage): ChatMessage {
    // Extract Message (if available)
    const messageMatch = agentMessage.content.match(/Message:\s*([\s\S]*?)(?=\n\s*(Total Count:|ResultType:|--- BEGIN CSV DATA---)|$)/);
    agentMessage.message = messageMatch ? messageMatch[1].trim() : '';
    // Extract Total Count (if available)
    const totalCountMatch = agentMessage.content.match(/Total Count:\s*(\d+)/);
    agentMessage.totalCount = totalCountMatch ? parseInt(totalCountMatch[1], 10) : 0;

    // Extract ResultType (if available)
    const resultTypeMatch = agentMessage.content.match(/ResultType:\s*(\d+)/);
    // ✅ Extract CustomerNumber (works even if CSV comes after it)
    const customerNumberMatch = agentMessage.content.match(/CustomerNumber:\s*(\d+)/);
    agentMessage.impersonateWith = customerNumberMatch ? customerNumberMatch[1].trim() : '';
    agentMessage.searchResultType = resultTypeMatch ? parseInt(resultTypeMatch[1], 10) : undefined;
    if (!resultTypeMatch && /--- Result ---|--- BEGIN CSV DATA---|--- END CSV DATA---/.test(agentMessage.content)) agentMessage.searchResultType = SearchResultType.Standard;
    if (agentMessage.searchResultType == undefined) {
      agentMessage.searchResultType = SearchResultType.FreeText;
      agentMessage.freeText = this.getFreeText(agentMessage.content);
    } else if (agentMessage.searchResultType == SearchResultType.FreeText) agentMessage.freeText = this.getFreeText(agentMessage.message);
    else {
      agentMessage.searchResultItems = this.getSearchResultItems(agentMessage.content);
    }
    return agentMessage;
  }

  getFreeText(content: string): string {
    let text = content
      .replace(/--- Result ---[\s\S]*?(?=--- BEGIN CSV DATA---|string|$)/g, '') // Remove result metadata if present
      .replace(/--- BEGIN CSV DATA---[\s\S]*?--- END CSV DATA---/g, '') // Remove CSV data block if present
      .trim();

    return this.linkifyMarkdown(text);
  }

  onContentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a');
    if (anchor && anchor.href && !anchor.href.startsWith('javascript:')) {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    }
  }

  private getDocumentLinkTitle(urlStr: string): string | null {
    if (!urlStr) return null;
    let cleanStr = urlStr.trim().replace(/^\[+|\]+$/g, '');

    const docExtRegex = /\.(pdf|docx?|xlsx?|pptx?|txt|csv|rtf|odt|ods|odp|zip|rar|7z|png|jpe?g|svg|gif)($|\?|#)/i;

    try {
      const urlObj = new URL(cleanStr.startsWith('www.') ? `http://${cleanStr}` : cleanStr);

      // Check query parameters for document filename
      for (const [, val] of urlObj.searchParams.entries()) {
        if (docExtRegex.test(val)) {
          const parts = val.split(/[\/\\]/);
          return decodeURIComponent(parts[parts.length - 1]);
        }
      }

      // Check path for document filename
      const segments = urlObj.pathname.split('/').filter((s) => s.length > 0);
      if (segments.length > 0) {
        const lastSegment = decodeURIComponent(segments[segments.length - 1]);
        if (lastSegment && docExtRegex.test(lastSegment)) {
          return lastSegment;
        }
      }

      return null;
    } catch {
      const docMatch = cleanStr.match(/\/([^\/?#]+\.(pdf|docx?|xlsx?|pptx?|txt|csv|rtf|odt|ods|odp|zip|rar|7z|png|jpe?g|svg|gif))($|\?|#)/i);
      if (docMatch && docMatch[1]) {
        return decodeURIComponent(docMatch[1]);
      }
      return null;
    }
  }

  private linkifyMarkdown(text: string): string {
    if (!text) return '';

    // Convert markdown links with raw URL as title if it is a document link: [http.../file.pdf](href) -> [file.pdf](href)
    text = text.replace(/\[\s*(https?:\/\/[^\s\]]+|www\.[^\s\]]+)\s*\]\(([^)]+)\)/gi, (_match, rawUrlText, href) => {
      const docTitle = this.getDocumentLinkTitle(rawUrlText) || this.getDocumentLinkTitle(href);
      const title = docTitle ? docTitle : rawUrlText;
      return `[${title}](${href})`;
    });

    // Convert unlinked raw URLs to markdown links: document link -> [file.pdf](href), other -> [url](href)
    text = text.replace(/(?<!\[[^\]]*)(https?:\/\/[^\s<]+|www\.[^\s<]+)(?![^\[]*\])/gi, (url) => {
      const href = url.startsWith('www.') ? `http://${url}` : url;
      const docTitle = this.getDocumentLinkTitle(url);
      const title = docTitle ? docTitle : url;
      return `[${title}](${href})`;
    });

    // Convert emails to mailto: markdown links if not already linked
    text = text.replace(/(?<!\[[^\]]*)([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?![^\[]*\])/gi, (email) => {
      return `[${email}](mailto:${email})`;
    });

    // Convert phone numbers to tel: markdown links
    const phoneRegex = /\+?\d{1,4}(?:[\s\-\.]?\d{1,5})+/g;
    text = text.replace(phoneRegex, (match) => {
      const trailingPunctMatch = match.match(/^(.*?)([\s;.,!?]*)$/);
      const phonePart = trailingPunctMatch ? trailingPunctMatch[1] : match;
      const punctPart = trailingPunctMatch ? trailingPunctMatch[2] : '';

      const digitsOnly = phonePart.replace(/[^\d+]/g, '');
      if (digitsOnly.length >= 7) {
        return `[${phonePart}](tel:${digitsOnly})${punctPart}`;
      }
      return match;
    });

    return text;
  }

  editPrompt(index: number, message: ChatMessage) {
    this.editModeEnable = undefined;
    message.content = this.userPrompt;
    this.messageHistory.messages.splice(index + 2); // add 2 bcz we skip first 2 rows
    this.chatWithAI(this.userPrompt, true);
  }

  private scrollToBottom() {
    setTimeout(() => {
      if (this.dialog) {
        // Get the dialog content element
        const dialogContent = this.dialog.el.nativeElement.querySelector('.p-dialog-content');
        if (dialogContent) {
          dialogContent.scrollTo({ top: dialogContent.scrollHeight, behavior: 'smooth' });
        }
      }
    }, 300);
  }

  public enableEditMode(index: number, userPrompt: string) {
    this.editModeEnable = index;
    this.userPrompt = userPrompt;
  }

  public disableEditMode() {
    this.userPrompt = '';
    this.editModeEnable = undefined;
  }

  isSearchResultNotEmpty(): boolean {
    return this.messageHistory && this.messageHistory?.messages?.length > 1;
  }
  ngOnDestroy() {
    if (this.chatSubscription) {
      this.chatSubscription.unsubscribe();
    }
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  private getSearchResultItems(content: string): SearchResultItem[] {
    let csvData = '';

    if (content.includes('--- BEGIN CSV DATA---')) {
      const splitContent = content.split('--- BEGIN CSV DATA---');
      csvData = splitContent[1]?.split('--- END CSV DATA---')[0] || '';

      csvData = csvData.replace(/```/g, '').replace(/csv/g, '').trim(); // Remove formatting artifacts

      const lines = csvData
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line); // Trim & remove empty lines

      if (lines.length < 2) return []; // Ensure at least header + one data row

      // Extract all Document Numbers and their Read Only statuses from the result section
      const documentReadOnlyMap = new Map<string, boolean>();
      const documentLines = content.match(/DocumentNumber:\s*([\w\/]+),\s*ReadOnly:\s*(True|False)/gi);
      if (documentLines) {
        documentLines.forEach((line) => {
          const match = line.match(/DocumentNumber:\s*([\w\/]+),\s*ReadOnly:\s*(True|False)/i);
          if (match) {
            const docNumber = match[1].trim();
            const readOnly = match[2].toLowerCase() == 'true';
            documentReadOnlyMap.set(docNumber, readOnly);
          }
        });
      }

      const headers = lines[0].split(';').map((header) => header.trim()); // Get headers
      const dataRows = lines.slice(1); // Get data rows

      const temp: SearchResultItem[] = dataRows.map((row) => {
        const values = row.split(';').map((value) => value.trim()); // Ensure values are split correctly
        const mappedItem: Partial<SearchResultItem> = {};
        headers.forEach((header, index) => {
          var value = values[index] || ''; // Get value or empty string
          value = value != 'null' ? value : '';
          switch (header) {
            case 'CompanyNo':
              mappedItem.companyNo = value;
              break;
            case 'DocumentTyp':
            case 'DocumentType':
              mappedItem.documentType = value;
              break;
            case 'Belegnummer':
            case 'DocumentNo':
              mappedItem.documentNo = value;
              break;
            case 'Nettowert':
            case 'ValueNet':
              mappedItem.valueNet = value ? parseFloat(value) : undefined;
              break;
            case 'Bruttowert':
            case 'ValueGross':
              mappedItem.valueGross = value ? parseFloat(value) : undefined;
              break;
            case 'Währung':
            case 'DocumentCurrency':
              mappedItem.documentCurrency = value;
              break;
            case 'Besteller':
            case 'Orderer':
              mappedItem.orderer = value;
              break;
            case 'Datum':
            case 'Date':
              mappedItem.date = value ? new Date(value) : undefined;
              break;
            case 'Lieferadresse':
            case 'DeliveryAddress':
              mappedItem.deliveryAddress = value;
              break;
            case 'InvoiceAddress':
              mappedItem.invoiceAddress = value;
              break;
            case 'DocumentUrl':
              mappedItem.documentUrl = value;
              break;
            case 'DocumentStatus':
              mappedItem.documentStatus = value as any;
              break;
            case 'Beschreibung':
            case 'Description':
              mappedItem.description = value;
              break;
            case 'Position':
              mappedItem.position = value;
              break;
            case 'Artikelbezeichnung':
            case 'Artikelbeschreibung':
            case 'ArticleDescription':
              mappedItem.articleDescription = value;
              break;
            case 'ArticleLine':
              mappedItem.articleLine = value;
              break;
            case 'Artikelnummer':
            case 'ArticleNo':
              mappedItem.articleNo = value;
              break;
            case 'UnitPrice':
              mappedItem.unitPrice = value ? parseFloat(value) : undefined;
              break;
            case 'Menge':
            case 'Quantity':
              mappedItem.quantity = value ? parseFloat(value) : undefined;
              break;
            case 'microsoftTenantID':
            case 'MicrosoftTenantID':
              mappedItem.microsoftTenantID = value;
              break;
            case 'ownerCompanyName':
            case 'OwnerCompanyName':
              mappedItem.ownerCompanyName = value;
              break;
            case 'ownerName':
            case 'OwnerName':
              mappedItem.ownerName = value;
              break;
            case 'EditionName':
            case 'editionName':
              mappedItem.editionName = value;
              break;
            case 'Type':
            case 'type':
              mappedItem.type = value;
              break;
            case 'Name':
            case 'name':
              mappedItem.name = value;
              break;
            case 'Title':
            case 'title':
              mappedItem.title = value;
              break;
            case 'Email':
            case 'email':
              mappedItem.email = value;
              break;
            case 'Phone':
            case 'phone':
              mappedItem.phone = value;
              break;
            case 'Mandant':
            case 'mandant':
              mappedItem.mandant = value;
              break;
            case 'isPdfArchive':
            case 'IsPdfArchive':
              mappedItem.isPdfArchive = value?.toLowerCase() === 'true' ? true : false;
              break;
            default:
              if (header) {
                mappedItem[header] = value;
              }
              break;
          }
        });

        // Set readonly flag based on the document number from the map
        if (mappedItem.documentNo && documentReadOnlyMap.has(mappedItem.documentNo)) {
          mappedItem.readOnly = documentReadOnlyMap.get(mappedItem.documentNo)!;
        } else {
          mappedItem.readOnly = false;
        }

        return mappedItem as SearchResultItem;
      });
      return temp; // Return processed data
    }

    return []; // Return empty array if no valid CSV data
  }

}
